/**
 * ActionItem — Checkoff component for daily actions.
 * Checkbox + action text + thread dot + purpose (expandable).
 * Completed items get strikethrough + green check.
 */

import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, MoreHorizontal, Trash2, ArrowRight, Sparkles } from 'lucide-react';

export default function ActionItem({ action, onToggle, onDelete, loading }) {
  const [expanded, setExpanded] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const isCompleted = action.status === 'completed';
  const isSkipped = action.status === 'skipped';
  const isDeferred = action.status === 'deferred';
  const isDone = isCompleted || isSkipped || isDeferred;
  const isMomentum = action.is_committed === false;
  const isAiSuggested = action.suggested_by === 'ai';

  const handleCheck = () => {
    if (loading) return;
    onToggle(action.id, isCompleted ? 'pending' : 'completed');
  };

  const handleSkip = () => {
    onToggle(action.id, 'skipped');
    setShowMenu(false);
  };

  const handleDefer = () => {
    onToggle(action.id, 'deferred');
    setShowMenu(false);
  };

  return (
    <div className={`group border rounded-lg transition-all duration-200 animate-fade-in-up ${
      action.is_top_priority
        ? 'border-blue-700/50 bg-blue-950/20'
        : isMomentum
          ? 'border-dashed border-slate-700/40 bg-slate-800/20'
          : 'border-slate-700/40 bg-slate-800/30'
    } ${isDone && !isCompleted ? 'opacity-60' : ''} ${isMomentum && !isDone ? 'opacity-80' : ''} ${isCompleted ? 'border-green-800/40' : ''}`}>
      <div className="flex items-start gap-2 px-3 py-3">
        {/* Checkbox — wrapped in larger tap target for mobile */}
        <button
          onClick={handleCheck}
          disabled={loading}
          aria-label={isCompleted ? 'Mark incomplete' : 'Mark complete'}
          aria-pressed={isCompleted}
          className="-ml-1 flex-shrink-0 min-w-tap min-h-tap sm:min-w-0 sm:min-h-0 sm:w-7 sm:h-7 flex items-center justify-center group/check"
        >
          <span className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
            isCompleted
              ? 'bg-green-600 border-green-600 scale-100'
              : 'border-slate-600 group-hover/check:border-slate-400'
          }`}>
            {isCompleted && <Check size={12} className="text-white animate-check-pop" />}
          </span>
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {action.thread_color && (
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: action.thread_color }}
              />
            )}
            <span className={`text-sm ${
              isCompleted ? 'line-through text-slate-500' :
              isSkipped ? 'line-through text-slate-600' :
              isDeferred ? 'text-slate-500 italic' :
              'text-slate-200'
            }`}>
              {action.action_text}
            </span>
            {action.is_top_priority && !isDone && (
              <span className="text-[11px] bg-blue-900/50 text-blue-300 px-1.5 py-0.5 rounded font-medium">
                TOP
              </span>
            )}
            {isMomentum && !isDone && (
              <span className="text-[11px] bg-amber-900/40 text-amber-400 px-1.5 py-0.5 rounded font-medium">
                BONUS
              </span>
            )}
            {isAiSuggested && !isDone && (
              <Sparkles size={12} className="text-purple-400 flex-shrink-0" />
            )}
            {isSkipped && (
              <span className="text-[11px] text-slate-500">skipped</span>
            )}
            {isDeferred && (
              <span className="text-[11px] text-amber-500 flex items-center gap-0.5">
                <ArrowRight size={10} /> tomorrow
              </span>
            )}
          </div>

          {/* Thread name */}
          {action.thread_name && (
            <span className="text-xs text-slate-500 mt-0.5 block">{action.thread_name}</span>
          )}

          {/* Expandable purpose/outcome */}
          {(action.outcome_text || action.purpose_text) && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-400 mt-1 py-1 -mx-1 px-1 rounded"
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              {expanded ? 'Hide details' : 'Why this matters'}
            </button>
          )}

          {expanded && (
            <div className="mt-2 space-y-1 text-xs text-slate-400 pl-1 border-l-2 border-slate-700 ml-0.5">
              {action.outcome_text && (
                <p><span className="text-slate-500 font-medium">Result:</span> {action.outcome_text}</p>
              )}
              {action.purpose_text && (
                <p><span className="text-slate-500 font-medium">Why:</span> {action.purpose_text}</p>
              )}
            </div>
          )}
        </div>

        {/* Action menu */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowMenu(!showMenu)}
            aria-label="Action options"
            aria-haspopup="menu"
            aria-expanded={showMenu}
            className="min-w-tap min-h-tap sm:min-w-0 sm:min-h-0 sm:w-8 sm:h-8 flex items-center justify-center text-slate-600 hover:text-slate-400 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity rounded-md hover:bg-slate-700/50"
          >
            <MoreHorizontal size={16} />
          </button>

          {showMenu && (
            <>
              <div className="fixed inset-0 z-10 bg-black/30 sm:bg-transparent animate-fade-in" onClick={() => setShowMenu(false)} />
              <div
                role="menu"
                className="z-20 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 w-full sm:w-44
                  fixed left-0 right-0 bottom-0 rounded-b-none rounded-t-xl pb-safe animate-slide-up
                  sm:absolute sm:left-auto sm:right-0 sm:top-full sm:bottom-auto sm:mt-1 sm:rounded-lg sm:pb-1 sm:animate-fade-in"
              >
                {!isSkipped && (
                  <button
                    onClick={handleSkip}
                    role="menuitem"
                    className="w-full text-left px-4 py-3 sm:py-2 text-sm sm:text-xs text-slate-300 hover:bg-slate-700"
                  >
                    Skip
                  </button>
                )}
                {!isDeferred && (
                  <button
                    onClick={handleDefer}
                    role="menuitem"
                    className="w-full text-left px-4 py-3 sm:py-2 text-sm sm:text-xs text-slate-300 hover:bg-slate-700"
                  >
                    Defer to tomorrow
                  </button>
                )}
                <button
                  onClick={() => { onDelete(action.id); setShowMenu(false); }}
                  role="menuitem"
                  className="w-full text-left px-4 py-3 sm:py-2 text-sm sm:text-xs text-red-400 hover:bg-slate-700 flex items-center gap-2"
                >
                  <Trash2 size={13} /> Remove
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
